import React from 'react';
import { FixMeLater } from '../../utils';
import { useSticky } from './useSticky';
import { StickyAnchorPosition } from './Sticky.types';
import { STATIC_STYLES, useDynamicStyles } from './Sticky.styles';

export interface StickyProps {
    /**
     * The element that will be made sticky.
     */
    children: React.ReactNode;

    /**
     * The edge of the window to which the element will be anchored when stuck.
     * @default top
     */
    anchor?: StickyAnchorPosition;

    /**
     * Context to which sticky element should stick.
     * @default document.body
     */
    context?: Element;

    /**
     * Context to which 'scroll' and 'resize' events will be attached.
     * @default window
     */
    scrollContext?: Element | Window;

    /**
     * Offset in pixels from the top of the screen when fixing the element to viewport.
     * @default 0
     */
    offset?: number;

    /**
     * Offset in pixels from the bottom of the screen when fixing element to viewport.
     * @default 0
     */
    bottomOffset?: number;

    /**
     * If `true`, the sticky behaviour will be disabled.
     * @default false
     */
    disabled?: boolean;

    /**
     * Callback fired when the element sticks to the edge of the viewport.
     */
    onStick?: () => void;

    /**
     * Callback fired when the element becomes unstuck and returns to it's original position.
     */
    onUnstick?: () => void;

    /**
     * If `true`, the element will be 'pushed' by the viewport, attaching to the bottom of the screen when scrolling up.
     * @default false
     */
    push?: boolean;

    /**
     * If `true`, the element will be 100% width whilst stuck.
     * @default false
     */
    fullWidth?: boolean;

    /**
     * An offset that will be added to the default z-index for the sticky element.
     * This can be used to mange the visibility of overlapping sticky elements.
     * @default 0
     */
    zIndexOffset?: number;
}

export const Sticky: React.ForwardRefExoticComponent<StickyProps & React.RefAttributes<HTMLElement>> = React.forwardRef(
    function Sticky(props: StickyProps, ref: React.ForwardedRef<FixMeLater>): React.JSX.Element {
        const {
            children,
            anchor,
            context,
            scrollContext,
            offset,
            bottomOffset,
            onStick,
            onUnstick,
            disabled,
            push,
            fullWidth,
            zIndexOffset = 0,
            ...other
        } = props;

        const { sticky, triggerRef, stickyRef, stickyStyles, triggerStyles } = useSticky({
            disabled,
            scrollContext,
            context,
            anchor,
            push,
            offset,
            bottomOffset,
            onStick,
            onUnstick,
            fullWidth,
        });

        const cssVariables = useDynamicStyles(zIndexOffset);

        return (
            <div ref={ref} style={cssVariables} {...other}>
                <div ref={triggerRef} style={triggerStyles} />
                <div
                    ref={stickyRef}
                    css={STATIC_STYLES}
                    style={stickyStyles || undefined}
                    data-sticky={sticky ?? undefined}
                >
                    {children}
                </div>
            </div>
        );
    }
);
